import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  MODAL_OVERLAY_CLASS_NAME,
  MODAL_PANEL_EMPHASIS_CLASS_NAME,
} from './ModalPresentation';
import { acquireViewportScrollLock } from '../utils/viewportScrollLock';

interface ModalPortalProps {
  children: ReactNode;
}

export function ModalPortal({ children }: ModalPortalProps) {
  return createPortal(children, document.body);
}

type ModalRenderProps = {
  close: () => void;
  isClosing: boolean;
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode | ((props: ModalRenderProps) => ReactNode);
  panelClassName: string;
  labelledBy?: string;
  overlayClassName?: string;
  closeDurationMs?: number;
  onRequestClose?: () => void;
  trapFocus?: boolean;
  suspended?: boolean;
}

const DEFAULT_MODAL_CLOSE_DURATION_MS = 260;

function resolveModalCloseDuration(durationMs: number) {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 0
    : durationMs;
}

export function Modal({
  isOpen,
  onClose,
  children,
  panelClassName,
  labelledBy,
  overlayClassName = '',
  closeDurationMs = DEFAULT_MODAL_CLOSE_DURATION_MS,
  onRequestClose,
  trapFocus = false,
  suspended = false,
}: ModalProps) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const closeModal = useCallback((notifyParent = true) => {
    if (closeTimeoutRef.current) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      closeTimeoutRef.current = null;

      if (notifyParent) {
        onClose();
      }
    }, resolveModalCloseDuration(closeDurationMs));
  }, [closeDurationMs, onClose]);

  const requestClose = useCallback(() => {
    if (onRequestClose) {
      onRequestClose();
      return;
    }
    closeModal();
  }, [closeModal, onRequestClose]);

  useEffect(() => {
    if (isOpen) {
      if (!isClosing && closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      if (!isClosing) {
        setShouldRender(true);
        setIsClosing(false);
      }
      return;
    }

    if (shouldRender && !isClosing) {
      closeModal(false);
    }
  }, [closeModal, isClosing, isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender || suspended) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const panels = document.querySelectorAll('[data-modal-panel][role="dialog"]');
      if (panels[panels.length - 1] !== panelRef.current) return;
      if (event.key === 'Escape') {
        requestClose();
        return;
      }
      if (event.key === 'Tab' && trapFocus) {
        keepFocusInsidePanel(event, panelRef.current);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [requestClose, shouldRender, suspended, trapFocus]);

  useEffect(() => {
    if (!shouldRender || !trapFocus || suspended) return undefined;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => {
      const firstFocusable = focusableElements(panelRef.current)[0];
      (firstFocusable || panelRef.current)?.focus();
    });
    return () => {
      window.cancelAnimationFrame(focusFrame);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [shouldRender, suspended, trapFocus]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    return acquireViewportScrollLock();
  }, [shouldRender]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <ModalPortal>
      <div
        className={`fixed inset-0 z-[90] flex h-[var(--visual-viewport-height,100dvh)] w-dvw max-w-dvw items-start justify-center overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 [-webkit-overflow-scrolling:touch] ${MODAL_OVERLAY_CLASS_NAME} ${
          isClosing
            ? 'pointer-events-none animate-[modal-overlay-out_260ms_ease-in_forwards] motion-reduce:animate-none'
            : 'animate-[fade-in_220ms_ease-out] motion-reduce:animate-none'
        } ${overlayClassName}`}
        data-modal-overlay
        aria-hidden={suspended || undefined}
        onClick={requestClose}
      >
        <div className="my-auto flex w-full shrink-0 justify-center">
          <div
            ref={panelRef}
            className={`${panelClassName} ${MODAL_PANEL_EMPHASIS_CLASS_NAME} ${
              isClosing
                ? 'animate-[modal-panel-out_260ms_ease-in_forwards] motion-reduce:animate-none'
                : 'animate-[fade-in-scale_220ms_ease-out] motion-reduce:animate-none'
            }`}
            data-modal-panel
            role={suspended ? undefined : 'dialog'}
            aria-modal={suspended ? undefined : 'true'}
            aria-labelledby={suspended ? undefined : labelledBy}
            tabIndex={trapFocus ? -1 : undefined}
            onClick={(event) => event.stopPropagation()}
          >
            {typeof children === 'function'
              ? children({ close: () => closeModal(), isClosing })
              : children}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function focusableElements(panel: HTMLElement | null) {
  if (!panel) return [];
  return Array.from(panel.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
}

function keepFocusInsidePanel(event: KeyboardEvent, panel: HTMLElement | null) {
  const focusable = focusableElements(panel);
  if (!focusable.length) {
    event.preventDefault();
    panel?.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
